import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { API_ENDPOINTS } from './api';
import { blogCategoryFromSlug, extractIdFromSlug, buildSlug, toSlug } from './utils/slug';
import { sanitizeHtml, htmlToText, PAGE_BREAK_SELECTOR } from './utils/sanitizeHtml';
import './App.css';

function formatDate(dateStr) {
  if (!dateStr) return '';
  return dateStr.split('T')[0];
}

// Old posts were authored as markdown before the HTML editor existed; new
// posts come in as sanitised HTML from RichTextEditor. Content written as
// HTML always contains a tag, so this is enough to tell the two apart.
function isHtmlContent(text) {
  return /<[a-z][\s\S]*>/i.test(text || '');
}

function stripMetaLegacyMarkdown(text, maxLen = 160) {
  if (!text) return '';
  return text
    .replace(/<!--page-break-->/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\n/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .substring(0, maxLen)
}

function stripMeta(text, maxLen = 160) {
  if (!text) return '';
  if (isHtmlContent(text)) return htmlToText(text).substring(0, maxLen);
  return stripMetaLegacyMarkdown(text, maxLen);
}

function ensureProtocol(link) {
  if (!link || link === '#') return '#';
  if (/^https?:\/\//i.test(link)) return link;
  return `https://${link}`;
}

// Renders inline markdown: **bold**, *italic*, [text](url), bare URLs
function renderInline(text, keyOffset = 0) {
  if (!text) return null;
  const regex = /\*\*(.+?)\*\*|\*([^*\n]+?)\*|\[([^\]]+)\]\((https?:\/\/[^)]+)\)|https?:\/\/[^\s<>"]+/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = keyOffset;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    if (match[1] !== undefined) {
      parts.push(<strong key={key++}>{match[1]}</strong>);
    } else if (match[2] !== undefined) {
      parts.push(<em key={key++}>{match[2]}</em>);
    } else if (match[3] !== undefined) {
      parts.push(<a key={key++} href={ensureProtocol(match[4])} target="_blank" rel="noopener noreferrer" className="blog-inline-link">{match[3]}</a>);
    } else {
      let label;
      try { label = new URL(match[0]).hostname.replace(/^www\./, ''); }
      catch { label = match[0]; }
      parts.push(<a key={key++} href={ensureProtocol(match[0])} target="_blank" rel="noopener noreferrer" className="blog-inline-link">🔗 {label}</a>);
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

// Renders full content for LEGACY markdown posts: splits into lines, handles
// ## headings, blank lines, standalone images (![alt](url)) and inline formatting.
function renderMarkdownContent(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const imgLineRegex = /^!\[([^\]]*)\]\((https?:\/\/[^)]+)\)$/;
  return lines.map((line, i) => {
    const trimmed = line.trim();
    const imgMatch = trimmed.match(imgLineRegex);
    if (imgMatch) {
      return (
        <img
          key={i}
          src={imgMatch[2]}
          alt={imgMatch[1] || ''}
          className="blog-content-img"
          loading="lazy"
          decoding="async"
        />
      );
    }
    if (line.startsWith('## ')) return <h2 key={i} className="blog-content-h2">{renderInline(line.slice(3), i * 100)}</h2>;
    if (line.startsWith('# ')) return <h1 key={i} className="blog-content-h1">{renderInline(line.slice(2), i * 100)}</h1>;
    if (line.trim() === '') return <div key={i} style={{ height: '0.75em' }} />;
    return <p key={i} className="blog-content-p">{renderInline(line, i * 100)}</p>;
  });
}

// Splits LEGACY markdown content into multiple pages on the <!--page-break--> marker.
function splitMarkdownPages(text) {
  if (!text) return [''];
  const parts = text.split(/<!--page-break-->/g).map(p => p.trim()).filter(p => p.length > 0);
  return parts.length > 0 ? parts : [text];
}

// Splits current HTML content into multiple pages on the RichTextEditor's
// page-break marker div, without breaking any tags that straddle it.
function splitHtmlPages(html) {
  const clean = sanitizeHtml(html);
  if (!clean) return [''];
  const doc = new DOMParser().parseFromString(clean, 'text/html');
  const markers = Array.from(doc.querySelectorAll(PAGE_BREAK_SELECTOR));
  if (markers.length === 0) return [clean];

  const pages = [];
  let current = document.createElement('div');
  Array.from(doc.body.childNodes).forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE && node.matches?.(PAGE_BREAK_SELECTOR)) {
      pages.push(current.innerHTML.trim());
      current = document.createElement('div');
    } else {
      current.appendChild(node.cloneNode(true));
    }
  });
  pages.push(current.innerHTML.trim());
  return pages.filter(p => p.length > 0);
}

function BlogDetail() {
  const { categorySlug, slug } = useParams();
  const category = blogCategoryFromSlug(categorySlug);
  const id = extractIdFromSlug(slug);
  const navigate = useNavigate();
  const location = useLocation();
  const passedBlog = location.state?.blog || null;
  const [blog, setBlog] = useState(passedBlog);
  const [loading, setLoading] = useState(!passedBlog);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);

  const contentIsHtml = blog ? isHtmlContent(blog.content) : true;
  const pages = blog ? (contentIsHtml ? splitHtmlPages(blog.content) : splitMarkdownPages(blog.content)) : [''];
  const totalPages = pages.length;

  function goToPage(idx) {
    if (idx < 0 || idx >= totalPages) return;
    setCurrentPage(idx);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  useEffect(() => {
    if (passedBlog) return; // already have data, skip fetch
    async function fetchBlog() {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_ENDPOINTS.BLOGS}/${encodeURIComponent(category)}/${id}`);
        if (!response.ok) throw new Error('Blog not found');
        const data = await response.json();
        const raw = data.blog || data;
        setBlog({
          ...raw,
          blogId: raw.blogId || raw.id || raw._id,
          urlLink: raw.urlLink || '',
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchBlog();
  }, [category, id]);

  useEffect(() => {
    setCurrentPage(0);
  }, [blog?.blogId]);

  const Header = () => (
    <header className="modern-header">
      <div className="header-logo" style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
        <img src="/logo.svg" alt="OfferLelo Logo" />
        <span className="header-logo-text">OfferLelo</span>
      </div>
      <div className="header-actions">
        <button className="admin-btn" onClick={() => navigate('/blogs')}>← All Blogs</button>
      </div>
    </header>
  );

  if (loading) {
    return (
      <div className="blog-detail-page">
        <Header />
        <div style={{ textAlign: 'center', marginTop: 100, color: '#6366f1', fontSize: '1.1rem' }}>
          Loading blog...
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="blog-detail-page">
        {/*
          Must be noindex. The host serves unknown paths as the app shell with
          a 200 status, so a deleted or mistyped blog URL reaches this screen
          with a success code. Without this, Google records a "soft 404" —
          a page that says "not found" while claiming to be fine — which
          counts against the site.
        */}
        <Helmet>
          <title>Blog not found | OfferLelo</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <Header />
        <div className="blog-detail-container">
          <h1>Blog not found</h1>
          <button className="back-btn" onClick={() => navigate('/blogs')}>← Back to Blogs</button>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-detail-page">
      <Helmet>
        <title>{blog.title} | OfferLelo Blog</title>
        <meta name="description" content={stripMeta(blog.content) || blog.title} />
        <meta name="keywords" content={`${blog.category}, shopping tips, ${blog.category} buying guide, flipkart offer, amazon offer, myntra offer, ajio offer, deals, online shopping India, OfferLelo`} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta property="og:site_name" content="OfferLelo" />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:title" content={`${blog.title} | OfferLelo Blog`} />
        <meta property="og:description" content={stripMeta(blog.content) || blog.title} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://www.offerlelo.com/blog/${toSlug(blog.category)}/${buildSlug(blog.title, blog.blogId)}`} />
        <meta property="og:image" content={blog.image || 'https://www.offerlelo.com/logo192.png'} />
        <meta property="article:published_time" content={blog.createdOn || ''} />
        <meta property="article:modified_time" content={blog.updatedOn || blog.createdOn || ''} />
        <meta property="article:section" content={blog.category || ''} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${blog.title} | OfferLelo Blog`} />
        <meta name="twitter:description" content={stripMeta(blog.content) || blog.title} />
        <meta name="twitter:image" content={blog.image || 'https://www.offerlelo.com/logo192.png'} />
        <link rel="canonical" href={`https://www.offerlelo.com/blog/${toSlug(blog.category)}/${buildSlug(blog.title, blog.blogId)}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": blog.title,
          "description": stripMeta(blog.content) || blog.title,
          "image": blog.image || 'https://www.offerlelo.com/logo192.png',
          "author": { "@type": "Person", "name": blog.author || 'OfferLelo' },
          "publisher": { "@type": "Organization", "name": "OfferLelo", "logo": { "@type": "ImageObject", "url": "https://www.offerlelo.com/logo192.png" } },
          "datePublished": blog.createdOn || '',
          "dateModified": blog.updatedOn || blog.createdOn || '',
          "url": `https://www.offerlelo.com/blog/${toSlug(blog.category)}/${buildSlug(blog.title, blog.blogId)}`,
          "keywords": blog.category
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.offerlelo.com" },
            { "@type": "ListItem", "position": 2, "name": "Blogs", "item": "https://www.offerlelo.com/blogs" },
            { "@type": "ListItem", "position": 3, "name": blog.category, "item": `https://www.offerlelo.com/blogs?category=${toSlug(blog.category)}` },
            { "@type": "ListItem", "position": 4, "name": blog.title, "item": `https://www.offerlelo.com/blog/${toSlug(blog.category)}/${buildSlug(blog.title, blog.blogId)}` }
          ]
        })}</script>
      </Helmet>
      <Header />

      {/* Hero Section */}
      <div className="blog-detail-hero">
        <div className="blog-detail-hero-inner">
          <span className="blog-detail-category">{blog.category}</span>
          <h1 className="blog-detail-title">{blog.title}</h1>
          <div className="blog-detail-meta">
            <span className="blog-detail-author-avatar">{(blog.author || 'A')[0].toUpperCase()}</span>
            <span className="blog-detail-author">{blog.author}</span>
            <span className="blog-detail-separator">•</span>
            <span className="blog-detail-date">{formatDate(blog.createdOn)}</span>
          </div>
        </div>
      </div>

      <div className="blog-detail-container">
        {/* Featured Image */}
        {blog.image && (
          <div className="blog-detail-featured-image">
            <img
              src={blog.image}
              alt={blog.title}
              width="1200"
              height="630"
              loading="eager"
              fetchpriority="high"
              decoding="async"
            />
          </div>
        )}

        {/* Main blog text content */}
        <div className="blog-detail-content article-body">
          {contentIsHtml ? (
            // Sanitised by splitHtmlPages() just above — safe to render directly.
            // eslint-disable-next-line react/no-danger
            <div dangerouslySetInnerHTML={{ __html: pages[currentPage] }} />
          ) : (
            renderMarkdownContent(pages[currentPage])
          )}
        </div>

        {/* Multi-page navigation — only shown when the blog was split into pages */}
        {totalPages > 1 && (
          <div className="blog-detail-pagination">
            <button
              type="button"
              className="blog-page-nav-btn"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 0}
            >
              ‹ Prev
            </button>
            <div className="blog-page-dots">
              {pages.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={`blog-page-dot ${i === currentPage ? 'active' : ''}`}
                  onClick={() => goToPage(i)}
                  aria-label={`Go to page ${i + 1}`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="blog-page-nav-btn"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages - 1}
            >
              Next ›
            </button>
          </div>
        )}

        {/* Reference / external URL */}
        {blog.urlLink && blog.urlLink.trim() !== '' && (
          <a
            href={ensureProtocol(blog.urlLink)}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              margin: '28px 0',
              padding: '18px 24px',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              borderRadius: 12,
              textDecoration: 'none',
              boxShadow: '0 4px 16px rgba(99,102,241,0.3)',
              transition: 'opacity 0.2s',
            }}
          >
            <span style={{ fontSize: '1.4rem' }}>🔗</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: '1rem' }}>Visit Reference Link</div>
              <div style={{ color: '#c7d2fe', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {blog.urlLink}
              </div>
            </div>
            <span style={{ color: '#fff', fontSize: '1.2rem', fontWeight: 700, flexShrink: 0 }}>→</span>
          </a>
        )}

        {/* Footer */}
        <div className="blog-detail-footer">
          <div className="blog-detail-share">
            <span>Share this article:</span>
            <button className="share-btn" onClick={() => navigator.clipboard.writeText(window.location.href)}>
              📋 Copy Link
            </button>
            <button className="share-btn" onClick={() => window.open(
              `https://twitter.com/intent/tweet?text=${encodeURIComponent(blog.title)}&url=${encodeURIComponent(window.location.href)}`,
              '_blank'
            )}>
              🐦 Twitter
            </button>
            <button className="share-btn" onClick={() => window.open(
              `https://wa.me/?text=${encodeURIComponent(blog.title + ' ' + window.location.href)}`,
              '_blank'
            )}>
              💬 WhatsApp
            </button>
          </div>
          <button className="back-home-btn" onClick={() => navigate('/blogs')}>← Back to All Blogs</button>
        </div>
      </div>
    </div>
  );
}

export default BlogDetail;
